import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SideNav from "../components/SideNav";
import {
  fetchLoanMetrics,
  fetchLoanApplications,
  fetchLoanDetail,
  fetchLoanSchedule,
  reviewLoanApplication,
  approveLoanApplication,
  rejectLoanApplication,
  requestLoanInfo,
} from "../actions/loanActions";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const today = new Date();
const yearAgo = new Date(today);
yearAgo.setFullYear(today.getFullYear() - 1);

const STATUS_COLORS = {
  Draft: "#6c757d",
  Submitted: "#17a2b8",
  PendingReview: "#fd7e14",
  UnderReview: "#6f42c1",
  AwaitingInfo: "#ffc107",
  Approved: "#28a745",
  OfferAccepted: "#20c997",
  Disbursing: "#007bff",
  Disbursed: "#155724",
  Rejected: "#dc3545",
};

const GUARANTOR_STATUS_COLORS = {
  Pending: "#fd7e14",
  Opened: "#17a2b8",
  Completed: "#28a745",
  Declined: "#dc3545",
  Replaced: "#6c757d",
};

const STATUSES = [
  "Draft",
  "Submitted",
  "PendingReview",
  "UnderReview",
  "AwaitingInfo",
  "Approved",
  "OfferAccepted",
  "Disbursing",
  "Disbursed",
  "Rejected",
];

const formatMoney = (amount) =>
  amount != null
    ? `₦${Number(amount).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`
    : "—";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const downloadCSV = (filename, headers, rows) => {
  const escape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => r.map(escape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const StatusBadge = ({ status }) => (
  <span
    style={{
      background: STATUS_COLORS[status] || "#6c757d",
      color: status === "AwaitingInfo" ? "#333" : "#fff",
      padding: "2px 10px",
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {status || "—"}
  </span>
);

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px solid #f0f0f0",
      gap: 12,
    }}
  >
    <span style={{ color: "#888", fontSize: 13, minWidth: 150, flexShrink: 0 }}>
      {label}
    </span>
    <span
      style={{
        color: "#222",
        fontSize: 13,
        fontWeight: 500,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value != null && value !== "" ? value : "—"}
    </span>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 8,
      border: "1px solid #e8e8e8",
      padding: "14px 16px",
      marginBottom: 12,
    }}
  >
    <div
      style={{
        fontWeight: 700,
        fontSize: 13,
        color: "#444",
        marginBottom: 10,
        paddingBottom: 6,
        borderBottom: "2px solid #f0f0f0",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const Loans = () => {
  const dispatch = useDispatch();
  const initRef = useRef(true);

  // Date range
  const [fromDate, setFromDate] = useState(fmtDate(yearAgo));
  const [toDate, setToDate] = useState(fmtDate(today));

  // List filters & pagination
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLoanMode, setFilterLoanMode] = useState("");

  // Detail drawer
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Inline action panel
  const [actionPanel, setActionPanel] = useState("");
  const [offeredAmount, setOfferedAmount] = useState("");
  const [offeredTenor, setOfferedTenor] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [infoNote, setInfoNote] = useState("");

  // Redux
  const { loading: metricsLoading, data: metricsData } =
    useSelector((state) => state.getLoanMetrics) || {};
  const {
    loading: listLoading,
    data: listData,
    error: listError,
  } = useSelector((state) => state.getLoanApplications) || {};
  const {
    loading: detailLoading,
    data: detailData,
    error: detailError,
  } = useSelector((state) => state.getLoanDetail) || {};
  const {
    loading: scheduleLoading,
    data: scheduleData,
    error: scheduleError,
  } = useSelector((state) => state.getLoanSchedule) || {};
  const { loading: reviewLoading, error: reviewError } =
    useSelector((state) => state.reviewLoan) || {};
  const { loading: approveLoading, error: approveError } =
    useSelector((state) => state.approveLoan) || {};
  const { loading: rejectLoading, error: rejectError } =
    useSelector((state) => state.rejectLoan) || {};
  const { loading: requestInfoLoading, error: requestInfoError } =
    useSelector((state) => state.requestLoanInfo) || {};

  useEffect(() => {
    if (initRef.current) {
      dispatch(fetchLoanMetrics());
      dispatch(fetchLoanApplications(1, 50, fmtDate(yearAgo), fmtDate(today)));
      initRef.current = false;
    }
  }, [dispatch]);

  const refetchList = () =>
    dispatch(
      fetchLoanApplications(page, 50, fromDate, toDate, filterStatus, filterLoanMode),
    );

  const handleApplyDates = () => {
    dispatch(fetchLoanMetrics(fromDate, toDate));
    setPage(1);
    dispatch(fetchLoanApplications(1, 50, fromDate, toDate, filterStatus, filterLoanMode));
  };

  const handleFilter = () => {
    setPage(1);
    dispatch(fetchLoanApplications(1, 50, fromDate, toDate, filterStatus, filterLoanMode));
  };

  const handleClearFilters = () => {
    setFilterStatus("");
    setFilterLoanMode("");
    setPage(1);
    dispatch(fetchLoanApplications(1, 50, fromDate, toDate));
  };

  const handlePageChange = (p) => {
    setPage(p);
    dispatch(fetchLoanApplications(p, 50, fromDate, toDate, filterStatus, filterLoanMode));
  };

  const handleView = (loan) => {
    setSelectedLoan(loan);
    setActionPanel("");
    setOfferedAmount("");
    setOfferedTenor("");
    setRejectReason("");
    setInfoNote("");
    dispatch(fetchLoanDetail(loan.id));
    dispatch(fetchLoanSchedule(loan.id));
    setDrawerOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedLoan(null);
    setActionPanel("");
    document.body.style.overflow = "";
  };

  const handleReview = async () => {
    try {
      await dispatch(reviewLoanApplication(selectedLoan.id));
      toast.success("Application moved to Under Review");
      dispatch(fetchLoanDetail(selectedLoan.id));
      refetchList();
      setActionPanel("");
    } catch (e) {
      toast.error(e.message || "Failed to start review");
    }
  };

  const handleApprove = async () => {
    const loanMode = detailData?.data?.loan_mode;
    const body =
      loanMode === 2
        ? { offered_amount: Number(offeredAmount), offered_tenor: Number(offeredTenor) }
        : {};
    try {
      await dispatch(approveLoanApplication(selectedLoan.id, body));
      toast.success("Loan approved");
      dispatch(fetchLoanDetail(selectedLoan.id));
      refetchList();
      dispatch(fetchLoanMetrics(fromDate, toDate));
      setActionPanel("");
    } catch (e) {
      toast.error(e.message || "Failed to approve loan");
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    try {
      await dispatch(rejectLoanApplication(selectedLoan.id, rejectReason));
      toast.success("Loan rejected");
      dispatch(fetchLoanDetail(selectedLoan.id));
      refetchList();
      dispatch(fetchLoanMetrics(fromDate, toDate));
      setActionPanel("");
      setRejectReason("");
    } catch (e) {
      toast.error(e.message || "Failed to reject loan");
    }
  };

  const handleRequestInfo = async () => {
    if (!infoNote.trim()) {
      toast.error("Please enter a note for the customer");
      return;
    }
    try {
      await dispatch(requestLoanInfo(selectedLoan.id, infoNote));
      toast.success("Info requested from customer");
      dispatch(fetchLoanDetail(selectedLoan.id));
      setActionPanel("");
      setInfoNote("");
    } catch (e) {
      toast.error(e.message || "Failed to request info");
    }
  };

  const handleDownloadCSV = () => {
    const apps = listData?.data || [];
    downloadCSV(
      "loans",
      ["Reference", "Borrower", "Type", "Amount", "Purpose", "Status", "Date Applied"],
      apps.map((a) => [
        a.reference_number,
        `${a.first_name || ""} ${a.surname || ""}`.trim(),
        a.loan_mode === 1 ? "Micro" : "Standard",
        a.amount,
        a.purpose,
        a.status,
        formatDate(a.date_created),
      ]),
    );
  };

  const apps = listData?.data || [];
  const pagination = listData?.pagination || {};
  const metrics = metricsData?.data || {};
  const loan = detailData?.data || {};
  const schedule = scheduleData?.data || [];

  const canReview = ["Submitted", "PendingReview"].includes(loan.status);
  const canApprove = ["UnderReview", "AwaitingInfo"].includes(loan.status);
  const canReject = ["UnderReview", "AwaitingInfo", "Approved"].includes(loan.status);
  const canRequestInfo = loan.status === "UnderReview";
  const hasActions = canReview || canApprove || canReject || canRequestInfo;

  const isDisbursed = ["Disbursed", "Disbursing"].includes(loan.status);

  const metricCards = [
    { label: "Total", value: metrics.total, color: "#007bff" },
    { label: "Pending Review", value: metrics.pending_review, color: "#fd7e14" },
    { label: "Under Review", value: metrics.under_review, color: "#6f42c1" },
    { label: "Awaiting Info", value: metrics.awaiting_info, color: "#e0a800" },
    { label: "Approved / Disbursed", value: (metrics.approved || 0) + (metrics.disbursed || 0), color: "#28a745" },
    { label: "Rejected", value: metrics.rejected, color: "#dc3545" },
  ];

  return (
    <div className="main-container">
      <SideNav />
      <div className="main-content">
        <div style={{ padding: "24px" }}>

          {/* Page header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <h2 style={{ fontWeight: 700, margin: 0, fontSize: 22 }}>Loans</h2>
            <button
              onClick={handleDownloadCSV}
              style={{
                background: "#28a745",
                color: "#fff",
                border: "none",
                padding: "8px 18px",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Download CSV
            </button>
          </div>

          {/* Date range */}
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 20,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div>
              <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>
                From
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>
                To
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
              />
            </div>
            <button
              onClick={handleApplyDates}
              style={{
                background: "#007bff",
                color: "#fff",
                border: "none",
                padding: "7px 18px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Apply
            </button>
          </div>

          {/* Metric cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))",
              gap: 12,
              marginBottom: 24,
            }}
          >
            {metricCards.map((c) => (
              <div
                key={c.label}
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  padding: "16px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  borderLeft: `4px solid ${c.color}`,
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>
                  {metricsLoading ? "—" : c.value ?? 0}
                </div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{c.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 16,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, minWidth: 160 }}
            >
              <option value="">All Statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filterLoanMode}
              onChange={(e) => setFilterLoanMode(e.target.value)}
              style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, minWidth: 130 }}
            >
              <option value="">All Types</option>
              <option value="1">Micro</option>
              <option value="2">Standard</option>
            </select>
            <button
              onClick={handleFilter}
              style={{ background: "#007bff", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
            >
              Filter
            </button>
            <button
              onClick={handleClearFilters}
              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
            >
              Clear
            </button>
          </div>

          {/* Table */}
          {listLoading ? (
            <p style={{ textAlign: "center", padding: 40, color: "#888" }}>Loading applications...</p>
          ) : listError ? (
            <p style={{ color: "red", textAlign: "center" }}>{listError}</p>
          ) : apps.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: "#aaa" }}>No loan applications found.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                  borderRadius: 8,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    {["Reference", "Borrower", "Type", "Amount", "Purpose", "Status", "Date Applied", ""].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: "10px 14px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#555",
                            borderBottom: "2px solid #e8e8e8",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {apps.map((app, i) => (
                    <tr key={app.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>
                        {app.reference_number || "—"}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        {`${app.first_name || ""} ${app.surname || ""}`.trim() || "—"}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span
                          style={{
                            background: app.loan_mode === 1 ? "#e3f2fd" : "#f3e5f5",
                            color: app.loan_mode === 1 ? "#1565c0" : "#6a1b9a",
                            padding: "2px 8px",
                            borderRadius: 10,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {app.loan_mode === 1 ? "Micro" : "Standard"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                        {formatMoney(app.amount)}
                      </td>
                      <td style={{ padding: "10px 14px" }}>{app.purpose || "—"}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <StatusBadge status={app.status} />
                      </td>
                      <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                        {formatDate(app.date_created)}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <button
                          onClick={() => handleView(app)}
                          style={{
                            background: "#007bff",
                            color: "#fff",
                            border: "none",
                            padding: "5px 12px",
                            borderRadius: 5,
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div style={{ display: "flex", gap: 6, marginTop: 16, justifyContent: "center", flexWrap: "wrap" }}>
              {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 5,
                    border: "1px solid #ccc",
                    background: p === page ? "#007bff" : "#fff",
                    color: p === page ? "#fff" : "#333",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Drawer */}
      {drawerOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "flex-end",
          }}
          onClick={(e) => e.target === e.currentTarget && closeDrawer()}
        >
          <div
            style={{
              background: "#f4f5f7",
              width: "min(680px, 100vw)",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflowY: "hidden",
            }}
          >
            {/* Drawer header */}
            <div
              style={{
                background: "#fff",
                padding: "16px 20px",
                borderBottom: "1px solid #e8e8e8",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#222" }}>
                  {loan.reference_number || selectedLoan?.reference_number || "Application Detail"}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  {(loan.loan_mode || selectedLoan?.loan_mode) && (
                    <span
                      style={{
                        background:
                          (loan.loan_mode || selectedLoan?.loan_mode) === 1
                            ? "#e3f2fd"
                            : "#f3e5f5",
                        color:
                          (loan.loan_mode || selectedLoan?.loan_mode) === 1
                            ? "#1565c0"
                            : "#6a1b9a",
                        padding: "2px 8px",
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {(loan.loan_mode || selectedLoan?.loan_mode) === 1 ? "Micro" : "Standard"}
                    </span>
                  )}
                  <StatusBadge status={loan.status || selectedLoan?.status} />
                </div>
              </div>
              <button
                onClick={closeDrawer}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#777", lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {/* Drawer body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {detailLoading ? (
                <p style={{ textAlign: "center", padding: 48, color: "#888" }}>
                  Loading application...
                </p>
              ) : detailError ? (
                <p style={{ color: "red", textAlign: "center" }}>{detailError}</p>
              ) : (
                <>
                  {/* Borrower */}
                  <SectionCard title="Borrower">
                    <InfoRow
                      label="Full Name"
                      value={`${loan.first_name || ""} ${loan.middle_name || ""} ${loan.surname || ""}`.replace(/\s+/g, " ").trim()}
                    />
                    <InfoRow label="Phone" value={loan.phone} />
                    <InfoRow label="Email" value={loan.email} />
                    <InfoRow label="Date of Birth" value={formatDate(loan.date_of_birth)} />
                    <InfoRow label="Gender" value={loan.gender} />
                    <InfoRow label="BVN" value={loan.masked_bvn} />
                    <InfoRow label="NIN" value={loan.masked_nin} />
                  </SectionCard>

                  {/* Address */}
                  {loan.address && (
                    <SectionCard title="Address">
                      <InfoRow label="House No." value={loan.address?.house_number} />
                      <InfoRow label="Street" value={loan.address?.street} />
                      <InfoRow label="Estate / Landmark" value={loan.address?.estate || loan.address?.landmark} />
                      <InfoRow
                        label="City / LGA"
                        value={[loan.address?.city, loan.address?.lga].filter(Boolean).join(", ")}
                      />
                      <InfoRow label="State" value={loan.address?.state} />
                    </SectionCard>
                  )}

                  {/* Employment — Standard only */}
                  {loan.loan_mode === 2 && loan.employment && (
                    <SectionCard title="Employment">
                      <InfoRow label="Employer" value={loan.employment?.employer_name} />
                      <InfoRow label="Job Title" value={loan.employment?.job_title} />
                      <InfoRow label="Years Employed" value={loan.employment?.years_employed} />
                      <InfoRow label="Monthly Income" value={formatMoney(loan.employment?.monthly_income)} />
                    </SectionCard>
                  )}

                  {/* Loan Setup */}
                  <SectionCard title="Loan Setup">
                    <InfoRow label="Requested Amount" value={formatMoney(loan.amount)} />
                    <InfoRow
                      label="Tenor"
                      value={loan.tenor ? `${loan.tenor} month${loan.tenor !== 1 ? "s" : ""}` : null}
                    />
                    <InfoRow label="Purpose" value={loan.purpose} />
                    <InfoRow
                      label="Monthly Rate"
                      value={loan.monthly_rate != null ? `${(loan.monthly_rate * 100).toFixed(0)}%` : null}
                    />
                    <InfoRow label="Monthly Repayment" value={formatMoney(loan.monthly_repayment)} />
                    <InfoRow label="Total Interest" value={formatMoney(loan.total_interest)} />
                    <InfoRow label="Total Repayable" value={formatMoney(loan.total_repayable)} />
                    <InfoRow label="Processing Fee" value={formatMoney(loan.processing_fee)} />
                    <InfoRow label="Net to Receive" value={formatMoney(loan.net_to_receive)} />
                    {loan.loan_mode === 1 && (
                      <InfoRow
                        label="System Approved Amount"
                        value={
                          loan.system_approved_amount != null
                            ? formatMoney(loan.system_approved_amount)
                            : "Not yet assessed"
                        }
                      />
                    )}
                  </SectionCard>

                  {/* Credit & Bank */}
                  <SectionCard title="Credit & Bank">
                    <InfoRow
                      label="Bureau Score"
                      value={
                        loan.bureau_score != null
                          ? `${loan.bureau_score} (min ${loan.bureau_min_score ?? 90})`
                          : "Not checked"
                      }
                    />
                    <InfoRow label="Bureau Status" value={loan.bureau_status} />
                    <InfoRow label="Bank Linked" value={loan.bank_linked ? "Yes" : "No"} />
                    <InfoRow label="Bank" value={loan.bank_name} />
                    <InfoRow label="Account Name" value={loan.account_name} />
                    <InfoRow label="Eligibility" value={loan.eligibility_status} />
                    <InfoRow
                      label="Liveness Check"
                      value={
                        loan.liveness_passed === true
                          ? "Passed"
                          : loan.liveness_passed === false
                          ? "Failed"
                          : "Not done"
                      }
                    />
                    {loan.loan_mode === 2 && (
                      <InfoRow
                        label="Liveness Check 2"
                        value={
                          loan.liveness2_passed === true
                            ? "Passed"
                            : loan.liveness2_passed === false
                            ? "Failed"
                            : "Not done"
                        }
                      />
                    )}
                    <InfoRow label="Terms Signed" value={loan.signed ? "Yes" : "No"} />
                  </SectionCard>

                  {/* Documents */}
                  {loan.documents && loan.documents.length > 0 && (
                    <SectionCard title={`Documents (${loan.documents.length})`}>
                      {loan.documents.map((doc) => (
                        <div
                          key={doc.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span style={{ fontSize: 13, color: "#555" }}>
                            {(doc.document_type || "").replace(/_/g, " ")}
                          </span>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, color: "#007bff", fontWeight: 600 }}
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </SectionCard>
                  )}

                  {/* Contacts — Micro only */}
                  {loan.loan_mode === 1 && loan.contacts && loan.contacts.length > 0 && (
                    <SectionCard title="Emergency Contacts">
                      {loan.contacts.map((c, i) => (
                        <div
                          key={i}
                          style={{
                            paddingBottom: 8,
                            marginBottom: 8,
                            borderBottom:
                              i < loan.contacts.length - 1 ? "1px solid #f0f0f0" : "none",
                          }}
                        >
                          <InfoRow label="Name" value={c.full_name} />
                          <InfoRow label="Phone" value={c.phone_number} />
                          <InfoRow
                            label="Relationship"
                            value={c.relationship_other || c.relationship}
                          />
                        </div>
                      ))}
                    </SectionCard>
                  )}

                  {/* Guarantors — Standard only */}
                  {loan.loan_mode === 2 && (
                    <SectionCard title="Guarantors">
                      {!loan.guarantors || loan.guarantors.length === 0 ? (
                        <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>
                          No guarantors added yet
                        </p>
                      ) : (
                        loan.guarantors.map((g, i) => (
                          <div
                            key={g.id}
                            style={{
                              paddingBottom: 10,
                              marginBottom: 10,
                              borderBottom:
                                i < loan.guarantors.length - 1 ? "1px solid #f0f0f0" : "none",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 4,
                              }}
                            >
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{g.full_name}</span>
                              <span
                                style={{
                                  background: GUARANTOR_STATUS_COLORS[g.status] || "#6c757d",
                                  color: g.status === "Pending" ? "#333" : "#fff",
                                  padding: "2px 8px",
                                  borderRadius: 10,
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                {g.status}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: "#888" }}>
                              {g.email} · {g.phone_number}
                            </div>
                            {g.opened_at && (
                              <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                                Opened: {formatDate(g.opened_at)}
                              </div>
                            )}
                            {g.completed_at && (
                              <div style={{ fontSize: 11, color: "#aaa" }}>
                                Completed: {formatDate(g.completed_at)}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </SectionCard>
                  )}

                  {/* Collateral — Standard only */}
                  {loan.loan_mode === 2 && loan.collateral && (
                    <SectionCard title="Collateral">
                      <InfoRow label="Type" value={loan.collateral?.collateral_type} />
                      <InfoRow
                        label="Declared Value"
                        value={formatMoney(loan.collateral?.declared_value)}
                      />
                      <InfoRow label="Condition" value={loan.collateral?.condition} />
                      <InfoRow label="Location" value={loan.collateral?.location} />
                      <InfoRow label="Description" value={loan.collateral?.description} />
                    </SectionCard>
                  )}

                  {/* Offer — Standard post-approval */}
                  {loan.offer && (
                    <SectionCard title="Offer">
                      <InfoRow
                        label="Offered Amount"
                        value={formatMoney(loan.offer?.offered_amount)}
                      />
                      <InfoRow
                        label="Tenor"
                        value={
                          loan.offer?.offered_tenor
                            ? `${loan.offer.offered_tenor} month${loan.offer.offered_tenor !== 1 ? "s" : ""}`
                            : null
                        }
                      />
                      <InfoRow
                        label="Monthly Repayment"
                        value={formatMoney(loan.offer?.offer_monthly_repayment)}
                      />
                      <InfoRow
                        label="Total Repayable"
                        value={formatMoney(loan.offer?.offer_total_repayable)}
                      />
                      <InfoRow label="Expires" value={formatDate(loan.offer?.offer_expires_at)} />
                      <InfoRow label="Customer Decision" value={loan.offer?.offer_decision} />
                    </SectionCard>
                  )}

                  {/* Disbursement */}
                  {loan.disbursement && (
                    <SectionCard title="Disbursement">
                      <InfoRow
                        label="Processing Fee"
                        value={formatMoney(loan.disbursement?.processing_fee)}
                      />
                      <InfoRow
                        label="Net Disbursed"
                        value={formatMoney(loan.disbursement?.net_disbursed)}
                      />
                      <InfoRow
                        label="Disbursed At"
                        value={formatDate(loan.disbursement?.disbursed_at)}
                      />
                      <InfoRow
                        label="First Repayment"
                        value={formatDate(loan.disbursement?.first_repayment_date)}
                      />
                    </SectionCard>
                  )}

                  {/* Repayment Schedule */}
                  {isDisbursed && (
                    <SectionCard title="Repayment Schedule">
                      {scheduleLoading ? (
                        <p style={{ fontSize: 13, color: "#888", margin: 0 }}>
                          Loading schedule...
                        </p>
                      ) : scheduleError ? (
                        <p style={{ fontSize: 13, color: "red", margin: 0 }}>{scheduleError}</p>
                      ) : schedule.length === 0 ? (
                        <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>
                          No schedule available
                        </p>
                      ) : (
                        <div style={{ overflowX: "auto" }}>
                          <table
                            style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
                          >
                            <thead>
                              <tr style={{ background: "#f8f9fa" }}>
                                {["#", "Due Date", "Principal", "Interest", "Total", "Status"].map(
                                  (h) => (
                                    <th
                                      key={h}
                                      style={{
                                        padding: "6px 8px",
                                        textAlign: "left",
                                        fontWeight: 600,
                                        color: "#555",
                                        borderBottom: "1px solid #e8e8e8",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {h}
                                    </th>
                                  ),
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {schedule.map((row) => (
                                <tr key={row.installment_number}>
                                  <td style={{ padding: "6px 8px" }}>
                                    {row.installment_number}
                                  </td>
                                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                                    {formatDate(row.due_date)}
                                  </td>
                                  <td style={{ padding: "6px 8px" }}>
                                    {formatMoney(row.principal_amount)}
                                  </td>
                                  <td style={{ padding: "6px 8px" }}>
                                    {formatMoney(row.interest_amount)}
                                  </td>
                                  <td style={{ padding: "6px 8px" }}>
                                    {formatMoney(row.total_amount)}
                                  </td>
                                  <td style={{ padding: "6px 8px" }}>
                                    <span
                                      style={{
                                        background:
                                          row.status === "Paid" ? "#d4edda" : "#fff3cd",
                                        color:
                                          row.status === "Paid" ? "#155724" : "#856404",
                                        padding: "2px 6px",
                                        borderRadius: 8,
                                        fontSize: 11,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {row.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </SectionCard>
                  )}

                  {/* Rejection reason */}
                  {loan.reject_reason && (
                    <SectionCard title="Rejection Reason">
                      <p style={{ fontSize: 13, color: "#555", margin: 0 }}>{loan.reject_reason}</p>
                    </SectionCard>
                  )}

                  {/* Actions */}
                  {hasActions && (
                    <SectionCard title="Actions">
                      {/* Button row */}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: actionPanel ? 14 : 0 }}>
                        {canReview && actionPanel !== "review" && (
                          <button
                            onClick={() => setActionPanel("review")}
                            style={{ background: "#6f42c1", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                          >
                            Start Review
                          </button>
                        )}
                        {canApprove && actionPanel !== "approve" && (
                          <button
                            onClick={() => setActionPanel("approve")}
                            style={{ background: "#28a745", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                          >
                            Approve
                          </button>
                        )}
                        {canRequestInfo && actionPanel !== "request-info" && (
                          <button
                            onClick={() => setActionPanel("request-info")}
                            style={{ background: "#fd7e14", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                          >
                            Request Info
                          </button>
                        )}
                        {canReject && actionPanel !== "reject" && (
                          <button
                            onClick={() => setActionPanel("reject")}
                            style={{ background: "#dc3545", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                          >
                            Reject
                          </button>
                        )}
                      </div>

                      {/* Start Review confirm */}
                      {actionPanel === "review" && (
                        <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 14 }}>
                          <p style={{ fontSize: 13, margin: "0 0 12px", color: "#333" }}>
                            Move this application to <strong>Under Review</strong>?
                          </p>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={handleReview}
                              disabled={reviewLoading}
                              style={{ background: "#6f42c1", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              {reviewLoading ? "Processing..." : "Confirm"}
                            </button>
                            <button
                              onClick={() => setActionPanel("")}
                              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              Cancel
                            </button>
                          </div>
                          {reviewError && (
                            <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>{reviewError}</p>
                          )}
                        </div>
                      )}

                      {/* Approve form */}
                      {actionPanel === "approve" && (
                        <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 14 }}>
                          {loan.loan_mode === 2 ? (
                            <>
                              <p style={{ fontSize: 13, margin: "0 0 12px", color: "#333" }}>
                                Set the <strong>offer terms</strong> for this Standard loan:
                              </p>
                              <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                                <div>
                                  <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>
                                    Offered Amount (₦)
                                  </label>
                                  <input
                                    type="number"
                                    value={offeredAmount}
                                    onChange={(e) => setOfferedAmount(e.target.value)}
                                    placeholder="e.g. 20000000"
                                    style={{ padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, width: 190 }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>
                                    Tenor (months)
                                  </label>
                                  <input
                                    type="number"
                                    value={offeredTenor}
                                    onChange={(e) => setOfferedTenor(e.target.value)}
                                    placeholder="1 – 6"
                                    min={1}
                                    max={6}
                                    style={{ padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, width: 100 }}
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            <p style={{ fontSize: 13, margin: "0 0 12px", color: "#333" }}>
                              Approve this <strong>Micro loan</strong>? This action approves and
                              disburses to the customer's PennyTree balance.
                            </p>
                          )}
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={handleApprove}
                              disabled={
                                approveLoading ||
                                (loan.loan_mode === 2 && (!offeredAmount || !offeredTenor))
                              }
                              style={{ background: "#28a745", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              {approveLoading ? "Processing..." : "Confirm Approval"}
                            </button>
                            <button
                              onClick={() => setActionPanel("")}
                              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              Cancel
                            </button>
                          </div>
                          {approveError && (
                            <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>{approveError}</p>
                          )}
                        </div>
                      )}

                      {/* Request Info form */}
                      {actionPanel === "request-info" && (
                        <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 14 }}>
                          <p style={{ fontSize: 13, margin: "0 0 8px", color: "#333" }}>
                            What information do you need from the customer?
                          </p>
                          <textarea
                            value={infoNote}
                            onChange={(e) => setInfoNote(e.target.value)}
                            rows={3}
                            placeholder="Describe what's needed..."
                            style={{ width: "100%", padding: "8px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button
                              onClick={handleRequestInfo}
                              disabled={requestInfoLoading}
                              style={{ background: "#fd7e14", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              {requestInfoLoading ? "Sending..." : "Send Request"}
                            </button>
                            <button
                              onClick={() => setActionPanel("")}
                              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              Cancel
                            </button>
                          </div>
                          {requestInfoError && (
                            <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>{requestInfoError}</p>
                          )}
                        </div>
                      )}

                      {/* Reject form */}
                      {actionPanel === "reject" && (
                        <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 14 }}>
                          <p style={{ fontSize: 13, margin: "0 0 8px", color: "#333" }}>
                            Provide a reason for rejection:
                          </p>
                          <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                            placeholder="Rejection reason..."
                            style={{ width: "100%", padding: "8px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
                          />
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button
                              onClick={handleReject}
                              disabled={rejectLoading}
                              style={{ background: "#dc3545", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              {rejectLoading ? "Processing..." : "Confirm Rejection"}
                            </button>
                            <button
                              onClick={() => setActionPanel("")}
                              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                            >
                              Cancel
                            </button>
                          </div>
                          {rejectError && (
                            <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>{rejectError}</p>
                          )}
                        </div>
                      )}
                    </SectionCard>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Loans;
